// The account panel's body (Phase 4, item 6): a left icon rail -- Scout, Company, Outreach,
// Research, History -- and the tab it selects. Panels stay mounted so selections, lookups and
// chat drafts survive switching tabs.
import { useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { BookAccount, BookPayload, EnrichCandidate } from '../api/types';
import type { WorkReason } from '../book/derived';
import { useAuthContext } from '../auth/AuthContext';
import { accountChatStarters, aeDealChatStarters, oversightChatStarters } from '../lib/chatStarters.ts';
import { EMPTY_CHAT_AE, EMPTY_CHAT_OVERSIGHT } from '../lib/emptyStates.ts';
import { ChatPanel } from './ChatEmbed';
import { CompanyTab } from './CompanyTab';
import { HistoryTab } from './HistoryTab';
import { OutreachTab } from './OutreachTab';
import { refreshPrompt } from './overviewSource';
import type { PanelTab } from './panelFacts';
import { RailIcon } from './panelParts';
import { ResearchTab } from './ResearchTab';
import { AeCompanyInfo } from '../ae/AeCompanyInfo';
import { OversightCompanyInfo } from '../oversight/OversightCompanyInfo';
import { panelForPage } from '../lib/roleViews.ts';
import type { PanelView } from '../lib/roleViews.ts';

export function AccountPanel({ row, book, onContactEnriched, workReason, view = panelForPage('board') }: {
  row: BookAccount;
  book: BookPayload;
  onContactEnriched: (contactId: string, candidate: EnrichCandidate) => void;
  workReason?: WorkReason | null;
  // The page's rail, Company info and Scout prompts (`lib/roleViews.ts`). The rest is the same.
  view?: PanelView;
}) {
  const tabs = view.panelTabs;
  const { getToken } = useAuthContext();
  const [activeTab, setActiveTab] = useState<PanelTab>('Company');
  // The chat mounts on the first visit, then stays: its composer sizes itself on mount, and a
  // hidden tab measures zero.
  const [scoutVisited, setScoutVisited] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  // The account chat's starters: refresh, then why-them and who-to-write-to. A click drafts the
  // prompt, the strategist sends it.
  // An AE gets deal prompts instead, the oversight page who-and-what prompts.
  const chatStarters = view.scoutStarters === 'ae'
    ? aeDealChatStarters(row.account)
    : view.scoutStarters === 'oversight'
      ? oversightChatStarters(row.account)
      : accountChatStarters(row.account, [{
      title: row.company_research ? 'Refresh research' : 'Research this company',
      prompt: refreshPrompt(row),
    }]);

  function selectTab(tab: PanelTab, focus = false) {
    setActiveTab(tab);
    if (tab === 'Scout') setScoutVisited(true);
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    if (focus) document.getElementById(`panel-tab-${tab.toLowerCase()}`)?.focus();
  }

  // Up and down move along the rail, like the tabs' arrow keys did.
  function navigateRail(event: KeyboardEvent<HTMLButtonElement>, tab: PanelTab) {
    const index = tabs.indexOf(tab);
    let nextIndex = index;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') nextIndex = (index + tabs.length - 1) % tabs.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = tabs.length - 1;
    else return;
    event.preventDefault();
    selectTab(tabs[nextIndex]!, true);
  }

  function pane(tab: PanelTab) {
    return {
      role: 'tabpanel' as const,
      id: `panel-pane-${tab.toLowerCase()}`,
      'aria-labelledby': `panel-tab-${tab.toLowerCase()}`,
      className: 'panelpane',
      hidden: activeTab !== tab,
    };
  }

  return (
    <div className="panelshell">
      <nav className="panelrail" role="tablist" aria-label="Account sections" aria-orientation="vertical">
        {tabs.map(tab => (
          <button key={tab} type="button" role="tab" id={`panel-tab-${tab.toLowerCase()}`}
            aria-controls={`panel-pane-${tab.toLowerCase()}`} aria-selected={activeTab === tab}
            tabIndex={activeTab === tab ? 0 : -1}
            onClick={() => selectTab(tab)} onKeyDown={event => navigateRail(event, tab)}>
            <RailIcon tab={tab} />
            <span>{tab}</span>
          </button>
        ))}
      </nav>

      <div className="sidebody" ref={bodyRef}>
        <section {...pane('Scout')}>
          {book.viewer.sees_chat_embed
            ? (scoutVisited ? <ChatPanel row={row} starters={chatStarters}
              emptyLine={view.scoutStarters === 'ae' ? EMPTY_CHAT_AE
                : view.scoutStarters === 'oversight' ? EMPTY_CHAT_OVERSIGHT : undefined} /> : null)
            : <p className="panelempty">Scout&apos;s account chat is not on for you yet.</p>}
        </section>
        <section {...pane('Company')}>
          {view.companyInfo === 'ae' ? <AeCompanyInfo row={row} today={book.today} />
            : view.companyInfo === 'oversight' ? <OversightCompanyInfo row={row} today={book.today} />
              : <CompanyTab row={row} scoresVisible={Boolean(book.viewer.scores_visible)} />}
        </section>
        {tabs.includes('Outreach') ? (
          <section {...pane('Outreach')}>
            <OutreachTab row={row} book={book} getToken={getToken} active={activeTab === 'Outreach'}
              onContactEnriched={onContactEnriched} onOpenScout={() => selectTab('Scout')} />
          </section>
        ) : null}
        <section {...pane('Research')}><ResearchTab row={row} today={book.today} workReason={workReason} /></section>
        <section {...pane('History')}><HistoryTab row={row} /></section>
      </div>
    </div>
  );
}
